import React from "react";
import moment from "moment";
import * as Icon from "react-feather";
import { ExternalLink as IconExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

import UserService from "../../../services/UserService";
import EngagementService from "../../../services/EngagementService";

import UtilityService from "../../../services/UtilityService";

import { DeleteModal } from "./modals/DeleteModal";
import { UpdateModal } from "./modals/UpdateModal";

import RRuleEditor from "../../common/RRule/RRuleEditor";
import WComponent from "../../common/WComponent";
import Tasks from "../../tasks/Tasks";
import Notes from "../../notes/Notes";
import Events from "../../events/Events";
import NextEngagementModal from "../next_eng_modal/NextEngagementModal";
import TextFieldInput from "../../common/text_field_input/TextFieldInput";
import SearchableDropdown from "../../common/searchable_dropdown/SearchableDropdown";
import UserAssigner from "../../common/user_assigner/UserAssigner";
import UserConstants from "../../../constants/Users";
import EngagementTemplateModal from "../EngagementTemplates/eng_template__modal/EngagementTemplateModal";
import DateSelector from "../../common/date_selector/DateSelector";
import Button from "../../common/button/Button";
import Tabs from "../../common/tabs/Tabs";
import Tab from "../../common/tabs/tab/Tab";
import Checkbox from "../../common/checkbox/Checkbox";
import EngagementTaskList from "../../common/engagement_tasks/EngagamentTasks";
import Budgets from "../../budgets/Budgets";

import "react-datepicker/dist/react-datepicker.css";
import "./engagement.css";
import PermissionGate from '../../../permissions/permissionGate';
import RestrictedAccess from "../../common/restricted_access/RestrictedAccess";
import RequiredLabel from "../../common/required_label";
import { DeleteRecurringModal } from "./modals/DeleteRecurringModal";
import { NAME_CONVENTIONS } from "./engagementNameConventions";

const { alert } = UtilityService;
const NAME_CONVENTIONS_EDITMODE = (NAME_CONVENTIONS || []).filter(c => !c?.excludeInEditMode);

class Engagement extends WComponent {
	constructor() {
		super();
		this.state = {
			loading: true,
			engagement: null,
			search: "",
			errors: {},
			type: "t1",

			engagementId: 0,
			teamId: 0,
			clientId: 0,
			clientType: "",
			name: "",
			status: "",
			dateIn: new Date(),
			dueDate: new Date(),
			fiscalYearEnd: new Date(),
			notes: "",
			frequency: "",

			showDeleteModal: false,
			showNextModal: false,

			showUpdateModal: false,

			flow: {},
			flowCode: "t1",
			flowStates: [],
			view: "details",

			selectedTeamName: "",
			selectedClientName: "",
			selectedFlowStateName: "",
			selectedFlowStateId: 0,

			assignedUsers: [],

			rrule: "",
			recurrenceStartDate: new Date(),			
			engagementSnapshot: "",
			showCreateTemplateModal: false,
			taxEstimate: false,
			isNotAllowedResource: false,
			shouldDeleteRecurringEngagement: false,
			showRecurringDeleteModal: false,

			namingConvention: NAME_CONVENTIONS[0].id,
			flowName: "",
		};
	}

	componentDidMount = () => {
		this.resetComponent();
	};

	componentDidUpdate = prevProps => {
		if (prevProps.match.params.engagementId !== this.props.match.params.engagementId) {
			this.resetComponent();
		}
	};

	resetComponent = async () => {
		let { engagementId } = this.props.match.params;

		await this.update({ loading: true, engagementId });

		await this.fetchEngagement();
		await this.fetchTeam();

		await this.update({ loading: false });
	};

	fetchTeam = async () => {
		let { teamId } = this.state;

		let team = await UserService.fetchTeam({ teamId });

		if (!team) {
			return;
		}

		await this.update({ selectedTeamName: team.name });
	};


	inferNamingConventionFromEngagement = ({ name, fiscalYearEnd, dueDate, dateIn }) => {
		// Extract the last non-empty segment of the name after splitting on " - "
		// Example: "T2 Process - Ontario Corp - 2025-01-01" → "2025-01-01"
		const lastPart = (name || "")
			.split(" - ")
			.map((s) => s.trim())
			.filter(Boolean)
			.pop();

		// If no valid suffix is found, fall back to the default naming convention i.e., FYE
		if (!lastPart) return NAME_CONVENTIONS[0].id;

		// Format date into "YYYY-MM-DD" if valid, otherwise returns a falsy value
		const format = (d) => moment(d).isValid() && moment(d).format("YYYY-MM-DD");

		// Map of formatted date values to their corresponding naming convention IDs
		const map = {
			[format(dateIn)]: "DATE_IN",
			[format(dueDate)]: "DUE_DATE",
			[format(fiscalYearEnd)]: "FYE",
		};

		// Return the matching convention ID or default if no match is found
		return map[lastPart] || NAME_CONVENTIONS[0].id;
	};

	fetchEngagement = async () => {
		let { engagementId } = this.state;

		try {
			let { data: engagement } = await EngagementService.get({ engagementId });

			if(UtilityService.isNotAllowedResource({response : engagement})){
				await this.update({
					isNotAllowedResource : true,
					loading: false
				});	
				return;		
			}


			let assignedUsers = engagement.engagement_users.map(eu => {
				return {
					user_id: eu.user_id,
					role_id: eu.role_id,

					userName: `${eu.User.first_name} ${eu.User.last_name}`,
					roleName: `${eu.Role.name}`
				};
			});

			// Attach existing user title
			let tasks = engagement.tasks.map(task => {
				let roleName = null;

				if (task.task_users && task.task_users.length > 0) {
					roleName = task.task_users[0].Role.name;
				}

				return {
					...task,
					assignedUser: {
						name: roleName || UserConstants.roleType.NO_ROLE.value
					}
				};
			});

			const inferredNamingConvention = this.inferNamingConventionFromEngagement({
				name: engagement.name,
				fiscalYearEnd: engagement.fiscal_year_end,
				dueDate: engagement.due_date,
				dateIn: engagement.date_in
			});

			const namingConvention =
				engagement.naming_convention ||
				inferredNamingConvention;

			const flowName = engagement.flow?.name || "";

			await this.update({
				teamId: engagement.team_id || 0,
				clientId: engagement.Client ? engagement.Client.id : 0,
				clientType: engagement.Client ? engagement.Client.type : "",
				engagementId: engagement.id,
				flowId: engagement.flow_id,
				flowStateId: engagement.flow_state_id,
				flowCode: engagement.flow.code,
				
				flow: engagement.flow || {},
				flowName,
				namingConvention,
				
				name: engagement.name,
				status: engagement.status,
				dueDate: moment(engagement.due_date).toDate(),
				dateIn: moment(engagement.date_in).toDate(),
				fiscalYearEnd: moment(engagement.fiscal_year_end).toDate(),
				notes: engagement.notes,
				frequency: engagement.frequency,

				selectedClientName: engagement.Client ? engagement.Client.name : "No Assigned Client",
				selectedFlowStateName: engagement.flow_state.name,
				selectedFlowStateId: engagement.flow_state.id,

				assignedUsers,

				recurringEngagement: engagement.RecurringEngagement,
				recurrenceStartDate: this.extractRecurrenceStartDateFromRRule(engagement?.RecurringEngagement?.rrule),
				tasks,
				taxEstimate: engagement.tax_estimate
			});
		} catch (error) {
			console.log(error);
		}
	};

	updateEngagement = async () => {
		let { engagementId, teamId, clientId, flowStateId, name, namingConvention, dueDate, dateIn, fiscalYearEnd, notes, assignedUsers, loading, taxEstimate } = this.state;

		const missing = this.getMissingRequiredFields();
		if (missing.length) {
			alert({ type: "error", text: `Please fill: ${missing.join(", ")}` });
			return;
		}

		if (loading) {
			alert({ type: "info", text: "Updating..." });

			return;
		}

		await this.update({
			loading: true
		});

		try {
			dateIn = moment(dateIn).format("YYYY-MM-DD");
			dueDate = moment(dueDate).format("YYYY-MM-DD");
			fiscalYearEnd = moment(fiscalYearEnd).format("YYYY-MM-DD");

			let {
				data: { next }
			} = await EngagementService.update({
				engagementId,
				teamId,
				clientId,
				flowStateId,
				name,
				namingConvention,
				dueDate,
				dateIn,
				fiscalYearEnd,
				notes,
				assignedUsers,
				taxEstimate
			});

			alert({ type: "success", text: "Updated Successfully!" });

			if (next) {
				await this.update({
					showNextModal: true
				});
			}
		} catch (error) {
			alert({ type: "error", text: "Update Error!" });
		}

		await this.update({
			loading: false
		});
	};

	onDeleteEngagement = async () => {
		let { engagementId, shouldDeleteRecurringEngagement } = this.state;

		this.update({
			loading: true
		});

		let success = await EngagementService.deleteEngagement({ engagementId, shouldDeleteRecurringEngagement });

		if (success) {
			alert({ type: "success", text: "Deleted Successfully!" });
		} else {
			alert({ type: "error", text: "Delete Error!" });
		}

		this.update({
			loading: false
		});
	};

	onFlowStateSelect = flowState => {
		this.update({
			flowStateId: flowState.id,
			selectedFlowStateName: flowState.name
		});
	};

	onClientSelect = async (item) => {
		await this.update({
			clientId: item.id,
			clientType: item.type,
			selectedClientName: item.name
		});
		await this.setNameFromConvention({clientName: item.name});
	};

	onChange = event => {
		let { name, value, type, checked } = event.target;

		if (type === "checkbox") {
			value = checked;
		}

		this.update({
			[name]: value
		});
	};

	onBack = () => {
		this.props.history.goBack();
	};

	formatNameDate = (value) => {
		const m = moment(value);
		return m.isValid() ? m.format("YYYY-MM-DD") : "";
	};

	buildEngagementName = (overrides = {}) => {
		const {
			namingConvention = this.state.namingConvention,
			flowName = this.state.flowName || this.state.flow?.name,
			clientName = this.state.selectedClientName,

			fiscalYearEnd = this.state.fiscalYearEnd,
			dueDate = this.state.dueDate,
		} = overrides;

		let suffix = "";

		if (namingConvention === "DUE_DATE") suffix = this.formatNameDate(dueDate);
		else if (namingConvention === "YEAR") suffix = moment().format("YYYY");
		else if (namingConvention === "MONTH_YEAR") suffix = moment().format("MMMM YYYY"); 		
		else suffix = this.formatNameDate(fiscalYearEnd);

		return `${flowName || ""} - ${clientName || ""} - ${suffix}`;
	};

	setNameFromConvention = async (overrides = {}) => {
		const {
			namingConvention = this.state.namingConvention,
			fiscalYearEnd = this.state.fiscalYearEnd,
			dueDate = this.state.dueDate,
		} = overrides;

		let suffix = "";
		if (namingConvention === "DUE_DATE") suffix = this.formatNameDate(dueDate);
		else if (namingConvention === "YEAR") suffix = moment().format("YYYY");
		else if (namingConvention === "MONTH_YEAR") suffix = moment().format("MMMM YYYY");
		else suffix = this.formatNameDate(fiscalYearEnd);

		const currentName = this.state.name || "";
		const lastDashIndex = currentName.lastIndexOf(" - ");
		const nameWithoutSuffix = lastDashIndex !== -1 ? currentName.substring(0, lastDashIndex) : currentName;
		const name = `${nameWithoutSuffix} - ${suffix}`;

		await this.update({ name });
	};

	onNamingConventionSelect = async (item) => {
		if (!item?.id) return;

		await this.update({ namingConvention: item.id });
		await this.setNameFromConvention({ namingConvention: item.id });
	};

	onDateInSelect = async (date) => {
		await this.update({
			dateIn: date
		});
		await this.setNameFromConvention({ dateIn: date });
	};

	onDueDateSelect = async (date) => {
		await this.update({
			dueDate: date
		});
		await this.setNameFromConvention({ dueDate: date });
	};

	onFiscalYearEndSelect = async (date) => {
		await this.update({
			fiscalYearEnd: date
		});
		await this.setNameFromConvention({ fiscalYearEnd: date });
	};

	isDisabled() {
		return !this.isValid() || this.state.loading;
	}

	isValid = () => this.getMissingRequiredFields().length === 0;

	onConfirmModalOpen = () => {

		const { recurringEngagement } = this.state;

		if (recurringEngagement) {
			this.update({
				showRecurringDeleteModal: true,
			});
		} else {
			this.update({
				showDeleteModal: true,
				shouldDeleteRecurringEngagement: false,
			});
		}
	};

	onCloseRecurringDeleteModal = (confirmed) => {
		this.update({
			showRecurringDeleteModal: false,
			shouldDeleteRecurringEngagement: !!confirmed,
			showDeleteModal: true,
		});
	};

	onUpdateModalOpen = () => {
		this.update({
			showUpdateModal: true
		});
	};

	onUpdateModalClose = confirmed => {
		this.update({
			showUpdateModal: false
		});

		if (confirmed) {
			this.updateEngagement();
			this.updateRecurringEngagement();
		}
	};

	updateRecurringEngagement = async () => {
		const { recurringEngagement, teamId, assignedUsers, engagementId } = this.state;

		if (recurringEngagement) {
			let recurringEngagementSnapshot = JSON.parse(recurringEngagement.engagement_snapshot);
			if (teamId) {
				recurringEngagementSnapshot.teamId = teamId;
			}

			if (assignedUsers) {
				recurringEngagementSnapshot.assignedUsers = assignedUsers;
			}

			await EngagementService.updateRecurringEngagement({
				recurringEngagementId: recurringEngagement.id,
				rrule: recurringEngagement.rrule,
				engagementSnapshot: recurringEngagementSnapshot,
				engagementId
			});
		}
	};

	onCloseDeleteModal = confirmed => {
		let { type } = this.state;
		let { history } = this.props;

		this.update({
			showDeleteModal: false
		});

		if (confirmed) {
			this.onDeleteEngagement();
			history.push(`/engagements`);
		}
	};

	onCloseUpdateModal = () => {
		this.update({
			showUpdateModal: false
		});
	};

	switchView = view => {
		this.update({
			view: view.id
		});
	};

	onSelectTeam = async team => {
		await this.update({
			teamId: team.id,
			selectedTeamName: team.name
		});

		// fetch users associated with the team
		await this.onFetchUsersByTeam({ teamId: team.id });
	};

	onFetchUsersByTeam = async ({ teamId }) => {
		let team = await UserService.fetchTeam({ teamId });

		const defaultUsers = (team.users || []).filter(u => !!(u.isDefault));

		// update the assigend users of the selected new team
		await this.update({
			assignedUsers: defaultUsers
		});
	};

	onAssignedUsersChanged = async ({ selectedUsers }) => {
		await this.update({
			assignedUsers: selectedUsers
		});
	};

	onRRuleChange = async ({ rrule }) => {
		await this.update({ rrule });
	};

	onUpdateRecurringEngagementClicked = async () => {
		let { rrule, recurringEngagement, loading, engagementSnapshot, engagementId } = this.state;

		if (!rrule && recurringEngagement.rrule) {
			rrule = recurringEngagement.rrule;
		}
		
		if (rrule == "never") {
			rrule = "";
		}

		if (loading) {
			return;
		}

		await this.update({ loading: true });

		let toastId = alert({ type: "info", text: "Updating ...", timeout: 20000 });

		let success = await EngagementService.updateRecurringEngagement({
			recurringEngagementId: recurringEngagement.id,
			rrule,
			engagementSnapshot,
			engagementId
		});

		await this.update({ loading: false });

		alert({ type: "dismiss", id: toastId });

		if (!success) {
			alert({ type: "error", text: "Unable to update Recurring Settings..." });
		} else {
			alert({ type: "success", text: "Recurring Engagement updated!" });
		}
	};

	updateEngagementSnapshot = ({ taskSnapShot, snapshot }) => {
		let updatedSnapshot = { ...snapshot, tasks: taskSnapShot };
		this.update({ engagementSnapshot: updatedSnapshot });
	};

	getMissingRequiredFields = () => {
		const { name, clientId, flowId, flowStateId, dateIn, dueDate, fiscalYearEnd } = this.state;
		const missing = [];
		if (!name?.trim()) missing.push("Name");
		if (!clientId) missing.push("Client");
		if (!flowId) missing.push("Workflow Type");	
		if (!flowStateId) missing.push("Status");
		if (!fiscalYearEnd) missing.push("Fiscal Year End");
		if (!dateIn) missing.push("Date In");
		if (!dueDate) missing.push("Due Date");

		return missing;
	};

	extractRecurrenceStartDateFromRRule = (rruleString) => {
		try {
			if (!rruleString) return new Date();
			const dtLine = (rruleString || "").split(/\r?\n/).find((l) => l.startsWith("DTSTART:"));
			if (!dtLine) return new Date();
			const dt = dtLine.replace("DTSTART:", "").trim(); // e.g. 20240131T055238Z
			const parsed = moment.utc(dt, "YYYYMMDD[T]HHmmss[Z]", true);
			return parsed.isValid() ? parsed.toDate() : new Date();
		} catch (e) {
			return new Date();
		}
	};

	renderRepeats() {
		const { updateEngagementSnapshot } = this;
		let { recurringEngagement, recurrenceStartDate } = this.state;
		let rrule = recurringEngagement.rrule;
		let snapshot = JSON.parse(recurringEngagement.engagement_snapshot);
		let snapshotTasks = snapshot.tasks;

		return (
			<div className="engagement__repeats">
				<div className="re-parent">
					<div className="re-parent__editor">
						<RRuleEditor onChange={this.onRRuleChange} rrule={rrule} startDate={recurrenceStartDate}/>
					</div>
					<div className="re-parent__board">
						<EngagementTaskList
							tasks={snapshotTasks}
							updateEngagementSnapshot={taskSnapShot => updateEngagementSnapshot({ taskSnapShot, snapshot })}
						></EngagementTaskList>
					</div>
				</div>
				<div className="engagement__repeats__footer">
					<Button onClick={this.onUpdateRecurringEngagementClicked} text="Update" className={"ft-update-recurrence"} />
				</div>
			</div>
		);
	}

	renderDetails() {
		const {
			name,
			namingConvention,
			selectedClientName,
			clientType,
			clientId,
			selectedTeamName,
			flow,
			flowCode,
			selectedFlowStateName,
			dateIn,
			dueDate,
			fiscalYearEnd,
			assignedUsers,
			taxEstimate
		} = this.state;

		return (
			<div className="engagement">
				<div className="engagement__body">
					<div className="engagement__body__left">
						<TextFieldInput title={<RequiredLabel text="Name" />} name="name" onChange={this.onChange} autoComplete="off" value={name} className="ft-eng-det-name" />
						
						<SearchableDropdown
							containerClass="engagement__body__input"
							title="Naming Convention"
							options={NAME_CONVENTIONS_EDITMODE}
							selectedName={(NAME_CONVENTIONS_EDITMODE.find(x => x.id === namingConvention)?.name) || ""}
							onSelect={this.onNamingConventionSelect}
							showAll
							selectEmptyOnClear={false}
							className="ft-eng-det-nameconvention"
						/>

						<SearchableDropdown
							containerClass="engagement__body__input"
							title={<RequiredLabel text="Client" />}
							onSelect={this.onClientSelect}
							type={"clients"}
							selectedName={selectedClientName}
							className="ft-eng-det-client"
						/>

						<div className="engagement__body__left__view-client">
							<Link className="engagement__body__left__view-client__link" to={`/clients/${clientType}/${clientId}`} target="_blank">
								View Client
								<IconExternalLink className="engagement__body__left__view-client__link__icon" size={13} />
							</Link>
						</div>

						<SearchableDropdown
							containerClass="engagement__body__input"
							title="Team"
							onSelect={this.onSelectTeam}
							type={"teams"}
							selectedName={selectedTeamName}
							showAll
							className="ft-eng-form-team"
						/>

						<SearchableDropdown
							containerClass="engagement__body__input"
							title={<RequiredLabel text="Status" />}
							onSelect={this.onFlowStateSelect}
							flowCode={flowCode}
							firstFocus
							showAll
							type={"flowStates"}
							selectedName={selectedFlowStateName}
							className="ft-eng-form-status"
						/>

						<DateSelector
							containerClass="engagement__body__input"
							title={<RequiredLabel text="Date In" />}
							selectedDate={dateIn}
							timeIntervals={30}
							onDateSelect={this.onDateInSelect}
							dateFormat="MMMM d, yyyy"
							className="ft-eng-det-datein"
						/>

						<DateSelector
							containerClass="engagement__body__input"
							title={<RequiredLabel text="Due Date" />}
							selectedDate={dueDate}
							timeIntervals={30}
							onDateSelect={this.onDueDateSelect}
							dateFormat="MMMM d, yyyy"
							className="ft-eng-det-duedate"
						/>

						<DateSelector
							containerClass="engagement__body__input"
							title={<RequiredLabel text="Fiscal Year End" />}
							selectedDate={fiscalYearEnd}
							timeIntervals={15}
							onDateSelect={this.onFiscalYearEndSelect}
							dateFormat="MMMM d, yyyy"
							className="ft-eng-det-fiscyearend"
						/>

						<Checkbox
							containerClass="engagement__body__input"
							title="Tax Estimate"
							name="taxEstimate"
							checked={taxEstimate}
							onChange={this.onChange}
						/>
					</div>
					<div className="engagement__body__right">
						<UserAssigner onChange={this.onAssignedUsersChanged} users={assignedUsers} />
					</div>
				</div>
				<div className="btn-group">
				<PermissionGate resource="engagement" action="delete">
					<Button
						view="secondary"
						onClick={() => this.onConfirmModalOpen()}
						icon={<Icon.Trash2 size={22} />}
						className={"ft-eng-det-delete-template-btn"}
					/>
				</PermissionGate>
					<PermissionGate resource="engagement" action="createtemplate">
						<Button
							view="secondary"
							text="Create Template"
							onClick={() => {
								this.update({
									showCreateTemplateModal: true
								});
							}}
							className="ft-eng-det-create-template"
						/>
					</PermissionGate>
					{/* Example usage of Permission Gate */}
					<PermissionGate resource="engagement" action="update">
						<Button onClick={this.onUpdateModalOpen} text="Update" disabled={this.state.loading} />
					</PermissionGate>
				</div>
			</div>
		);
	}

	render() {
		let {
			// Toggles
			view,
			showDeleteModal,
			showUpdateModal,
			showNextModal,
			showRecurringDeleteModal,
			// Metadata
			flow,
			engagementId,
			clientId,

			name,

			recurringEngagement,
			showCreateTemplateModal,
			isNotAllowedResource,
			
		} = this.state;

		let { switchView } = this;

		if(isNotAllowedResource){
			return <RestrictedAccess />;
		}
	

		return (
			<div>
				<div className="eng">
					<div className="eng__hd">
						<div className="eng__hd__title">
							<div className="eng__hd__title__name">
								<div className="eng__hd__title__name__back" onClick={this.onBack}>
									<Icon.ArrowLeftCircle size={32} />
								</div>
								{name}
							</div>
						</div>
					</div>

					<Tabs onSelect={switchView} selected={view}>
							<Tab id={"details"} icon={<Icon.User size={14} strokeWidth={1.5} />} text="Details" />
						<PermissionGate resource="note" action="list">
							<Tab id={"notes"} icon={<Icon.FileText size={14} strokeWidth={1.5} />} text="Notes" />
						</PermissionGate>
						<PermissionGate resource="task" action="list">
							<Tab id={"tasks"} icon={<Icon.List size={14} strokeWidth={1.5} />} text="Tasks" />
						</PermissionGate>
						<PermissionGate resource="engagement" action="eventsView">
						<Tab id={"events"} icon={<Icon.Bell size={14} strokeWidth={1.5} />} text="Events" />
						</PermissionGate>
						<PermissionGate resource="engagement" action="repeatView">
							{recurringEngagement && <Tab id={"repeats"} icon={<Icon.Bell size={14} strokeWidth={1.5} />} text="Repeat" />}
						</PermissionGate>
						<PermissionGate resource="engagement" action="budgetsView">
							<Tab id={"budgets"} icon={<Icon.Clock size={14} strokeWidth={1.5} />} text="Budgets" />
						</PermissionGate>
					</Tabs>

					{view === "details" && this.renderDetails()}

					{view === "repeats" && this.renderRepeats()}
				</div>

				<DeleteRecurringModal show={showRecurringDeleteModal} onClose={this.onCloseRecurringDeleteModal} />

				<DeleteModal show={showDeleteModal} onClose={this.onCloseDeleteModal} />

				<UpdateModal show={showUpdateModal} onClose={this.onUpdateModalClose} />

				<EngagementTemplateModal
					show={showCreateTemplateModal}
					onCreate={() => {
						alert({ type: "info", text: "Created Template" });

						this.update({ showCreateTemplateModal: false });
					}}
					onClose={() => {
						this.update({ showCreateTemplateModal: false });
					}}
					engagementTemplateId={"new"}
					name=""
					selectedFlow={{
						flowType: {
							id: this.state.flow.id,
							name: this.state.flow.name,
							code: this.state.flowCode
						},
						flowState: {
							id: this.state.selectedFlowStateId,
							name: this.state.selectedFlowStateName
						}
					}}
					selectedTeam={{
						id: this.state.teamId,
						name: this.state.selectedTeamName
					}}
					tasks={this.state.tasks}
				/>

				{view === "notes" && <Notes engagementId={engagementId} clientId={clientId} {...this.props} />}
				{view === "tasks" && <Tasks title="Tasks" engagementId={engagementId} showViews={false} showFilters={false} {...this.props} />}
				{view === "events" && <Events raw={true} title="Events" engagementId={engagementId} {...this.props} />}
				{view === "budgets" && <Budgets raw={true} title="Budgets" engagementId={engagementId} {...this.props} />}
				{engagementId && <NextEngagementModal show={showNextModal} engagementId={engagementId} />}
			</div>
		);
	}
}

export default Engagement;
